/**
 * The wizard's request shapes and demo content, kept free of React so the exact
 * calls it claims to make can be asserted in tests.
 *
 * Every request the wizard runs is a real /api/v1 call — the same surface an
 * integrator writes against. The point of the wizard is that nothing is
 * simulated, so these builders double as the source of the curl shown on screen
 * and of the fetch that actually fires. If the two could drift, the demo would
 * be lying, so both come from here.
 */

export const DEMO_GROUP_PREFIX = 'wizard-demo';

// A group name unique to this account, so two people running the wizard never
// push to each other's screens through a shared group label.
export function demoGroupName(userId) {
  const suffix = String(userId || 'anon').replace(/[^a-zA-Z0-9]/g, '').slice(-6).toLowerCase();
  return `${DEMO_GROUP_PREFIX}-${suffix || 'anon'}`;
}

/**
 * The template both steps drive. Deliberately built so step 2 has something to
 * change without a re-push:
 *   - {{headline}} / {{tagline}} are per-device variables
 *   - the datafeed is bound to a shared data source
 * One template, two different kinds of live update.
 */
export function demoTemplate(sourceId) {
  return {
    version: 1,
    background: 'linear-gradient(135deg,#0f172a,#1e293b)',
    widgets: [
      {
        id: 'headline',
        type: 'text',
        x: 80, y: 70, w: 1760, h: 150,
        props: {
          text: '{{headline}}',
          fontSize: 104, fontWeight: '800', color: '#ffffff', textAlign: 'center',
        },
      },
      {
        id: 'tagline',
        type: 'text',
        x: 80, y: 235, w: 1760, h: 70,
        props: {
          text: '{{tagline}}',
          fontSize: 40, fontWeight: '500', color: '#f0a11c', textAlign: 'center',
        },
      },
      {
        id: 'feed',
        type: 'datafeed',
        x: 160, y: 350, w: 1600, h: 560,
        props: {
          sourceId: sourceId || '',
          layout: 'table',
          fontSize: 34,
          maxRows: 8,
          headerBg: 'rgba(240,161,28,0.18)',
          rowAltBg: 'rgba(255,255,255,0.04)',
          columns: [
            { key: 'item', header: 'ITEM', flex: '2', fontWeight: '600' },
            { key: 'detail', header: 'DETAIL', flex: '2', color: '#9aa7c4' },
            { key: 'value', header: 'VALUE', flex: '1', align: 'right' },
          ],
          sampleData: [
            { item: 'Waiting for data', detail: 'Run step 2 to push rows', value: '—' },
          ],
        },
      },
      {
        id: 'clock',
        type: 'clock',
        x: 1480, y: 960, w: 360, h: 80,
        props: { format: '24h', showDate: false, color: 'rgba(255,255,255,0.5)', fontSize: 34 },
      },
    ],
  };
}

// Starting variables, so the first push renders real words rather than raw
// {{placeholders}} — an unresolved template is the classic black-screen bug.
export const INITIAL_VARIABLES = {
  headline: 'Hello from the API',
  tagline: 'Pushed to both screens with one call',
};

// What step 2 changes them to, to prove a variable update needs no re-push.
export const STEP2_VARIABLES = {
  headline: 'Changed without re-pushing',
  tagline: 'One PUT /variables — the template never moved',
};

export const STEP2_ROWS = [
  { item: 'Orders today', detail: 'Live from your POS', value: '1,284' },
  { item: 'Queue', detail: 'Now serving A-042', value: '6 waiting' },
  { item: 'Kitchen', detail: 'Avg ticket time', value: '4m 12s' },
  { item: 'Deploys', detail: 'main → production', value: 'green' },
];

/* ------------------------------ requests ------------------------------ */
// Each returns { method, path, body?, label } — `label` is what the event log
// and the step header call it.

export const req = {
  // --- setup, run on load rather than as a step ---
  createTemplate: (json, name = 'Wizard demo board') => ({
    method: 'POST', path: '/api/v1/templates', body: { name, json },
    label: 'Save the demo template',
  }),

  createDataSource: (name = 'Wizard demo feed') => ({
    method: 'POST', path: '/api/v1/data-sources',
    // Push-only source: nothing polls it, step 2 pushes rows straight in.
    // A far-future refresh keeps the poller off it entirely.
    body: { name, url: 'https://example.com/wizard-demo-no-poll', refresh_seconds: 86400 },
    label: 'Create a push-model data source',
  }),

  joinGroup: (uuid, group, name) => ({
    method: 'PATCH', path: `/api/v1/devices/${encodeURIComponent(uuid)}`,
    body: { name, group },
    label: `Put ${name} in group "${group}"`,
  }),

  setVariables: (uuid, variables, replace = false) => ({
    method: 'PUT', path: `/api/v1/devices/${encodeURIComponent(uuid)}/variables`,
    body: { variables, replace },
    label: 'Update variables (no re-push)',
  }),

  // --- step 1 ---
  groupPush: (group, templateId) => ({
    method: 'POST', path: `/api/v1/groups/${encodeURIComponent(group)}/push`,
    body: { template_id: templateId },
    label: 'Push to every screen in the group',
  }),

  // --- step 2 ---
  pushData: (sourceId, data) => ({
    method: 'POST', path: `/api/v1/data-sources/${encodeURIComponent(sourceId)}/push`,
    body: { data },
    label: 'Push live rows to the data source',
  }),

  // --- drawer ---
  listDevices: () => ({
    method: 'GET', path: '/api/v1/devices',
    label: 'List your screens',
  }),

  setPlaylist: (uuid, items) => ({
    method: 'PUT', path: `/api/v1/devices/${encodeURIComponent(uuid)}/playlist`,
    body: { items },
    label: 'Start a rotating playlist',
  }),

  stopPlaylist: (uuid) => ({
    method: 'DELETE', path: `/api/v1/devices/${encodeURIComponent(uuid)}/playlist`,
    label: 'Stop the playlist',
  }),

  devicePush: (uuid, templateId, variables) => ({
    method: 'POST', path: `/api/v1/devices/${encodeURIComponent(uuid)}/push`,
    body: { template_id: templateId, ...(variables ? { variables } : {}) },
    label: 'Push to one screen',
  }),

  clear: (uuid) => ({
    method: 'POST', path: `/api/v1/devices/${encodeURIComponent(uuid)}/clear`,
    label: 'Blank the screen',
  }),

  power: (uuid, action) => ({
    method: 'POST', path: `/api/v1/devices/${encodeURIComponent(uuid)}/power`,
    body: { action },
    label: `Turn the TV ${action} over HDMI-CEC`,
  }),

  // Deliberate failures — the error model is content, not an accident.
  badKey: () => ({
    method: 'GET', path: '/api/v1/devices',
    label: 'Call with a bad key (expect 401)',
    keyOverride: 'puttv_not_a_real_key',
  }),

  missingDevice: () => ({
    method: 'GET', path: '/api/v1/devices/00000000-0000-0000-0000-000000000000',
    label: "Read someone else's screen (expect 404, not 403)",
  }),
};

/**
 * The curl the user can paste into their own terminal. Identical in method,
 * path and body to what the Run button fires — same descriptor, two renderings.
 * `key` is shown as $PUTTV_KEY until a real one has been minted, so the snippet
 * is copyable either way and no key ever ends up in a screenshot by accident.
 */
export function toCurl(request, { base, key } = {}) {
  const keyText = key || '$PUTTV_KEY';
  const lines = [];
  const url = `${base || ''}${request.path}`;
  lines.push(`curl -X ${request.method} ${url} \\`);
  lines.push(`  -H "X-API-Key: ${keyText}"`);
  if (request.body) {
    lines[lines.length - 1] += ' \\';
    lines.push(`  -H "Content-Type: application/json" \\`);
    lines.push(`  -d '${JSON.stringify(request.body, null, 2).split('\n').join('\n     ')}'`);
  }
  return lines.join('\n');
}

// One-line summary of a wire result for the event log.
export function describeResult(request, result) {
  const status = result.status === 0 ? 'network error' : result.status;
  return `${request.method} ${request.path} → ${status} (${result.ms}ms)`;
}
